import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, X, Send, Loader2, Flame } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genSession = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `xaluca-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Build a stable unique id for each chat message (prevents React index-as-key issues).
const mkMsgId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Strip the [SPA_REQUEST]{...}[/SPA_REQUEST] marker emitted by the concierge and return parsed payload.
const extractSpaIntent = (raw) => {
  const re = /\[SPA_REQUEST\]\s*({[\s\S]*?})\s*\[\/SPA_REQUEST\]/i;
  const match = raw.match(re);
  if (!match) return { cleaned: raw, spa: null };
  let spa = null;
  try {
    spa = JSON.parse(match[1]);
  } catch {
    return { cleaned: raw.replace(re, "").trim(), spa: null };
  }
  const required = ["full_name", "email", "hotel", "treatment"];
  if (!required.every((k) => spa && typeof spa[k] === "string" && spa[k].trim())) {
    return { cleaned: raw.replace(re, "").trim(), spa: null };
  }
  return { cleaned: raw.replace(re, "").trim(), spa };
};

const spaConfirmText = (lang, data) => {
  const when = [data.preferred_date, data.preferred_time].filter(Boolean).join(" · ");
  if (lang === "fr") {
    return `Demande de soin enregistrée — ${data.treatment} à ${data.hotel}${when ? " · " + when : ""}. Notre équipe spa vous confirmera par email.`;
  }
  if (lang === "es") {
    return `Solicitud de tratamiento registrada — ${data.treatment} en ${data.hotel}${when ? " · " + when : ""}. Nuestro equipo de spa le confirmará por correo.`;
  }
  return `Spa request received — ${data.treatment} at ${data.hotel}${when ? " · " + when : ""}. Our spa team will confirm by email.`;
};

// Pick a localised string from an {en, fr, es} dict — falls back to English.
const pickI18n = (dict, lang) => dict[lang] || dict.en;

const SPA_FAILED_COPY = {
  en: "We couldn't save your spa request. Our team will reach out shortly.",
  fr: "Nous n'avons pas pu enregistrer votre demande de spa. Notre équipe vous contactera.",
  es: "No pudimos registrar su solicitud de spa. Nuestro equipo le contactará.",
};

const CHAT_UNAVAILABLE_COPY = {
  en: "Our concierge is momentarily unavailable. Please try again.",
  fr: "Notre conciergerie est momentanément indisponible. Veuillez réessayer.",
  es: "Nuestro conserje no está disponible. Intente de nuevo.",
};

const ChatbotWidget = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => {
    const k = "xaluca_chat_session";
    let s = localStorage.getItem(k);
    if (!s) {
      s = genSession();
      localStorage.setItem(k, s);
    }
    return s;
  });
  const [messages, setMessages] = useState([
    { id: mkMsgId(), role: "assistant", content: t("chatbot.welcome") },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Allow other components to open the chat widget by dispatching a
  // custom event on `window`. Used by the per-card "virtual assistant"
  // buttons on property cards (PropertyCardGallery).
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("xaluca:open-chat", handler);
    return () => window.removeEventListener("xaluca:open-chat", handler);
  }, []);

  // Update welcome when lang changes (only first message)
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length && prev[0].role === "assistant") {
        return [{ id: prev[0].id || mkMsgId(), role: "assistant", content: t("chatbot.welcome") }, ...prev.slice(1)];
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { id: mkMsgId(), role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
        language: lang,
      });
      const raw = res.data.reply || "";
      const { cleaned, spa } = extractSpaIntent(raw);
      setMessages((m) => [...m, { role: "assistant", content: cleaned }]);

      if (spa) {
        try {
          const saved = await axios.post(`${API}/spa-requests`, {
            ...spa,
            language: lang,
            source: "concierge",
          });
          setMessages((m) => [
            ...m,
            {
              role: "system",
              kind: "spa-confirmed",
              content: spaConfirmText(lang, saved.data),
            },
          ]);
        } catch {
          setMessages((m) => [
            ...m,
            {
              role: "system",
              kind: "spa-failed",
              content: pickI18n(SPA_FAILED_COPY, lang),
            },
          ]);
        }
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: pickI18n(CHAT_UNAVAILABLE_COPY, lang),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-6 z-[60] w-14 h-14 rounded-full bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] shadow-2xl flex items-center justify-center transition-all duration-300"
        data-testid="chatbot-toggle"
        aria-label="Open AI Concierge"
      >
        {open ? <X strokeWidth={1.5} /> : <Sparkles strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          className="fixed bottom-44 right-6 z-[60] w-[92vw] max-w-md h-[70vh] max-h-[600px] flex flex-col bg-[#FDFBF7] border border-[#2C2621]/10 shadow-2xl backdrop-blur-xl"
          data-testid="chatbot-window"
        >
          <div className="px-5 py-4 border-b border-[#2C2621]/10 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1A1513] text-[#FDFBF7] flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="font-serif-x text-lg leading-none text-[#2C2621]">
                {t("chatbot.title")}
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#C16542] mt-1">
                {t("chatbot.sub")}
              </div>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto chat-scroll px-5 py-5 space-y-4 bg-[#F2EBE1]/30"
            data-testid="chatbot-messages"
          >
            {messages.map((m, i) => {
              if (m.role === "system") {
                return (
                  <div key={m.id || `sys-${i}`} className="flex justify-center" data-testid="chat-bubble-system">
                    <div
                      className={`max-w-[92%] px-4 py-3 text-xs leading-relaxed flex items-start gap-2 border ${
                        m.kind === "spa-failed"
                          ? "bg-[#FDFBF7] border-[#C16542]/40 text-[#A35133]"
                          : "bg-[#1A1513] border-[#1A1513] text-[#D4A373]"
                      }`}
                      data-testid={`chat-system-${m.kind || "info"}`}
                    >
                      <Flame className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span>{m.content}</span>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={m.id || `msg-${i}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#1A1513] text-[#FDFBF7]"
                        : "bg-[#FDFBF7] text-[#2C2621] border border-[#2C2621]/10"
                    }`}
                    data-testid={`chat-bubble-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#FDFBF7] text-[#5C5248] border border-[#2C2621]/10 px-4 py-3 text-sm flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                  <span>...</span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={send}
            className="border-t border-[#2C2621]/10 p-3 flex items-center gap-2 bg-[#FDFBF7]"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chatbot.placeholder")}
              className="flex-1 bg-transparent text-sm px-3 py-3 border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542]"
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-[#C16542] hover:bg-[#A35133] disabled:opacity-40 text-[#FDFBF7] w-11 h-11 flex items-center justify-center transition-colors"
              data-testid="chatbot-send"
              aria-label="Send"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Send className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
